import { brand } from '../../theme/themePrimitives';
import { CustomerObject } from '../../params/redux/customer';
import { formatDateWithTime } from '../../helper';
import { resendCustomerInvitationThunk } from '../../redux/thunks/customer';
import { settingSelectedCustomer } from '../../redux/slices/customer';
import { toast } from 'react-toastify';
import { useParams } from 'react-router';
import { AppDispatch, RootState } from '../../redux/store';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Delete, Edit } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';

interface CustomerTableProps {
    search: string;
    sort: boolean;
    setEditModal: () => void;
    setDeleteModal: () => void;
}

interface CustomersRow {
    id: number;
    name: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    lastLogin: string;
    isPrimary: 0 | 1;
}

const CustomerTableComponent = ({ search, sort, setEditModal, setDeleteModal }: CustomerTableProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();
    const { customers, loaders } = useSelector((state: RootState) => state.customer);
    // **Filter and Sort Logic**
    const filteredCustomers = customers.filter((customer: CustomerObject) => {
        const name = `${customer.first_name || ''} ${customer.last_name || ''}`.trim().toLowerCase();
        const email = customer.email?.toLowerCase() || '';
        const phone = '';
        return (
            !search ||
            name.includes(search.toLowerCase()) ||
            email.includes(search.toLowerCase()) ||
            phone.includes(search)
        );
    });

    const sortedCustomers = sort
        ? [...filteredCustomers].sort((a, b) => a.email.localeCompare(b.email))
        : filteredCustomers;

    const rows: CustomersRow[] = sortedCustomers.map((customer: CustomerObject) => ({
        id: customer.id,
        name: `${customer.first_name || ''} ${customer.last_name || ''}`.trim(),
        first_name: customer.first_name,
        last_name: customer.last_name,
        email: customer.email || '',
        phone: customer.phone_number || '-',
        lastLogin: customer.last_login ? formatDateWithTime(customer.last_login) : '-',
        isPrimary: customer.is_primary
    })) || [];

    const handleDeleteSelection = (value: CustomersRow) => {
        const selectedCustomer = customers.find((item: CustomerObject) => item.id === value.id);
        dispatch(settingSelectedCustomer(selectedCustomer));
        setDeleteModal();
    };

    const editDealHandler = (value: CustomersRow) => {
        const selectedCustomer = customers.find((item: CustomerObject) => item.id === value.id);
        dispatch(settingSelectedCustomer(selectedCustomer));
        setEditModal();
    };

    const resendInvitation = async (value: CustomersRow) => {
        try {
            // const data: InviteCustomerParams = {
            //     first_name: value.first_name,
            //     last_name: value.last_name,
            //     email: value.email,
            //     phone_number: value.phone,
            //     is_primary: value.isPrimary,
            // };
            await dispatch(resendCustomerInvitationThunk({ email: value.email, client_id: id ? parseInt(id as string, 10) : 0 })).unwrap();
            toast.success('Resend Invitation Success');
        } catch (e) {
            // Catch errors
        }
    };

    const columns: GridColDef<CustomersRow>[] = [
        {
            field: 'name',
            headerName: 'Name',
            flex: 1,
            renderCell: (params: GridRenderCellParams<CustomersRow, string>) => <Box display="flex" gap="10px" alignItems="center" my={1}>
                <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">{params.value}</Typography>
                {params.row.isPrimary === 1 && <Box px={2} borderRadius={1} py="0.1rem" bgcolor={brand[50]} color={brand[300]} height="max-content"><Typography variant="body1" fontWeight="bold">Primary</Typography></Box>}
            </Box>
        },
        { field: 'lastLogin', headerName: 'LAST LOGGED IN', flex: 1 },
        { field: 'email', headerName: 'EMAIL', flex: 1 },
        { field: 'phone', headerName: 'PHONE NUMBER', flex: 1 },
        {
            field: 'id',
            headerName: 'ACTIONS',
            flex: 1,
            renderCell: (params: GridRenderCellParams<CustomersRow, number>) => <Box display="flex" alignItems="center" gap="2px" my="5px">
                {params.row.lastLogin === '-' && <Button variant="outlined" onClick={() => resendInvitation(params.row)}>Resend Invitation</Button>}
                <IconButton onClick={() => editDealHandler(params.row)}>
                    <Edit fontSize="small" color="info" />
                </IconButton>
                {params.row.isPrimary === 0 && <IconButton onClick={() => handleDeleteSelection(params.row)}>
                    <Delete fontSize="small" color="error" />
                </IconButton>}
            </Box>
        }
    ];

    return <Box height="66vh" width="100%">
        <DataGrid
            rows={rows as CustomersRow[]}
            loading={loaders.isLoading}
            columns={columns}
            pagination
            initialState={{
                pagination: {
                    paginationModel: {
                        pageSize: 20
                    }
                }
            }}
            sx={{
                '& .MuiDataGrid-row--borderBottom': {
                    backgroundColor: `${brand[50]} !important`
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold'
                },
                '& .MuiDataGrid-columnHeaders': {
                    fontWeight: 'bold'
                },
                '& .MuiDataGrid-cell': {
                    fontSize: '14px'
                },
                'width': '100%',
                'overflowX': 'hidden'
            }}
        />
    </Box>;
};

export default CustomerTableComponent;